<template>
  <q-input
    ref="inputRef"
    :model-value="modelValue"
    class="find-input input-xs tw-flex-1 tw-bg-white"
    dense
    outlined
    :placeholder="placeholder"
    :loading="loading"
    :autofocus="autofocus"
    :debounce="debounce"
    @update:model-value="handleInput"
    @keydown="handleKeydown"
  >
    <template #prepend>
      <!-- Show search icon in left slot -->
      <octicon v-if="!loading" class="tw-w-4 tw-text-gray-dark" icon="search" />
    </template>
    <template #append>
      <div class="tw-flex tw-items-center tw-gap-3">
        <!-- Show keyboard hint always -->
        <keyboard-shortcut-hint
          v-if="!loading"
          shortcut="/"
          theme="gray-medium"
          aria-label="Press / to focus search"
        />
        <!-- Show clear button when has value and is clearable -->
        <octicon
          v-if="!loading && modelValue && clearable"
          class="tw-w-4 tw-cursor-pointer hover:tw-opacity-70"
          icon="x"
          @click="handleClear"
        />
      </div>
    </template>
  </q-input>
</template>

<script setup lang="ts">
import { ref, onMounted, nextTick } from 'vue';
import type { QInput } from 'quasar';
import KeyboardShortcutHint from '@/components/core/KeyboardShortcutHint.vue';

/**
 * FindInput - Standardized search input component for modals
 *
 * Features:
 * - Consistent "Find" placeholder with visual keyboard shortcut
 * - Keyboard navigation support (up/down arrows)
 * - Loading state
 * - Optional clear button
 * - Auto-focus support
 * - Debounce support
 *
 * @example
 * ```vue
 * <find-input
 *   v-model="searchQuery"
 *   :loading="isSearching"
 *   @navigate-up="focusFooter"
 *   @navigate-down="focusFirstItem"
 *   @submit="handleSearch"
 * />
 * ```
 */

interface Props {
  modelValue?: string;
  placeholder?: string;
  loading?: boolean;
  autofocus?: boolean;
  debounce?: number;
  clearable?: boolean;
}

interface Emits {
  (e: 'update:modelValue', value: string): void;
  (e: 'navigate-up'): void;
  (e: 'navigate-down'): void;
  (e: 'submit', value: string): void;
  (e: 'clear'): void;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  placeholder: 'Find',
  loading: false,
  autofocus: true,
  debounce: 0,
  clearable: false,
});

const emit = defineEmits<Emits>();

// Refs
const inputRef = ref<InstanceType<typeof QInput>>();

// Lifecycle
onMounted(async () => {
  if (props.autofocus) {
    await nextTick();
    focus();
  }
});

// Event handlers
function handleInput(value: string | number | null) {
  emit('update:modelValue', String(value || ''));
}

// Keyboard navigation handlers
// These allow arrow key navigation from the find input to the list items
function handleKeydown(event: KeyboardEvent) {
  switch (event.key) {
    case 'ArrowDown':
      // Navigate to first item in list
      event.preventDefault();
      event.stopPropagation();
      emit('navigate-down');
      break;
    case 'ArrowUp':
      // Navigate to last item in list (circular navigation)
      event.preventDefault();
      event.stopPropagation();
      emit('navigate-up');
      break;
    case 'Enter':
      // Submit the search
      event.preventDefault();
      event.stopPropagation();
      emit('submit', props.modelValue);
      break;
  }
}

function handleClear() {
  emit('update:modelValue', '');
  emit('clear');
  focus();
}

// Public methods
function focus() {
  inputRef.value?.focus();
}

function blur() {
  inputRef.value?.blur();
}

function select() {
  inputRef.value?.select();
}

// Expose public API
defineExpose({
  focus,
  blur,
  select,
  inputRef,
});
</script>

<style lang="scss" scoped>
// Shared height variable for components that connect to FindInput (e.g., view-mode-toggle)
// Export via CSS custom property so adjacent components can match this height
$find-input-height: 32px;

.find-input {
  --find-input-height: #{$find-input-height};
  min-width: 200px;
  width: 100%;

  :deep(.q-field__control) {
    height: $find-input-height;
  }

  :deep(input) {
    font-size: 13px;
  }
}
</style>
